import { useState, useEffect, useCallback } from 'react';
import { getUser } from '../api/users';
import { getApiErrorMessage } from '@/lib/api-error';
import type { UserDto } from '../types';

/**
 * Hook for fetching a single user by ID
 */
export function useUser(id: number | undefined) {
  const [data, setData] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const user = await getUser(id);
      setData(user);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch user'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchUser,
  };
}
